import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import api, { Campaign, resolveAssetUrl } from "../lib/api";
import { useTenant } from "../lib/tenant";

type CampaignType = "standard" | "html" | "image";

function getLocalizedCampaign(slide: Campaign, locale: string): Campaign {
  if (!slide.translations || slide.translations.length === 0) return slide;
  
  // Try exact match (e.g., 'ja')
  const match = slide.translations.find(t => t.locale.startsWith(locale));
  if (match) {
    return {
      ...slide,
      badge: match.badge ?? slide.badge,
      headline1: match.headline1 ?? slide.headline1,
      headline2: match.headline2 ?? slide.headline2,
      body: match.body ?? slide.body,
      ctaLabel: match.ctaLabel ?? slide.ctaLabel,
    };
  }
  
  // Try English fallback
  const enMatch = slide.translations.find(t => t.locale.startsWith('en'));
  if (enMatch) {
    return {
      ...slide,
      badge: enMatch.badge ?? slide.badge,
      headline1: enMatch.headline1 ?? slide.headline1,
      headline2: enMatch.headline2 ?? slide.headline2,
      body: enMatch.body ?? slide.body,
      ctaLabel: enMatch.ctaLabel ?? slide.ctaLabel,
    };
  }

  return slide;
}

export default function HeroCarousel() {
  const { i18n } = useTranslation();
  const { id: tenantId, tTenant } = useTenant();
  const { data: campaigns = [] } = useQuery<Campaign[]>({
    queryKey: ["campaigns"],
    queryFn: () => api.get("/campaigns").then((r) => Array.isArray(r.data) ? r.data : []),
  });

  const isChlorine = tenantId === "chlorine.art";

  const defaultSlide: Campaign = {
    id: "__default__",
    type: "standard",
    badge: tTenant("hero.badge"),
    headline1: tTenant("hero.headline1"),
    headline2: tTenant("hero.headline2"),
    body: tTenant("hero.sub"),
    ctaLabel: tTenant("hero.cta"),
    ctaUrl: "/shop",
    secondaryLabel: tTenant("hero.story"),
    secondaryUrl: "/about",
    htmlContent: null,
    imageUrl: null,
    active: true,
    sortOrder: 0,
    createdAt: "",
  };

  const slides = campaigns.length > 0 ? campaigns : [defaultSlide];
  const total = slides.length;

  const [active, setActive] = useState(0);
  const [animating, setAnimating] = useState(false);
  const paused = useRef(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  function goTo(index: number) {
    if (animating || index === active) return;
    setAnimating(true);
    setActive(index);
    setTimeout(() => setAnimating(false), 500);
  }

  function prev() { goTo((active - 1 + total) % total); }
  function next() { goTo((active + 1) % total); }

  useEffect(() => {
    if (active >= total) setActive(0);
  }, [total, active]);

  useEffect(() => {
    if (total <= 1) return;
    timerRef.current = setInterval(() => {
      if (!paused.current) {
        setAnimating(true);
        setActive((i) => (i + 1) % total);
        setTimeout(() => setAnimating(false), 500);
      }
    }, 5000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [total]);

  const slideRaw = slides[active];
  const slide = getLocalizedCampaign(slideRaw, i18n.language);
  const slideType: CampaignType = (slide as any).type ?? "standard";

  return (
    <section
      className={`relative overflow-hidden px-4 text-center transition-colors duration-1000 ${isChlorine ? "bg-white py-12" : "bg-gradient-to-br from-brand-50 via-white to-pink-50"}`}
      onMouseEnter={() => { paused.current = true; }}
      onMouseLeave={() => { paused.current = false; }}
    >
      {!isChlorine && <div className="absolute inset-0 pointer-events-none opacity-20 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-brand-300 via-transparent to-transparent" />}

      <div
        key={slide.id}
        className={`transition-opacity duration-500 ${animating ? "opacity-0" : "opacity-100"} relative max-w-5xl mx-auto`}
      >
        {slideType === "image" && slide.imageUrl && (
          <div className="py-8">
            <img src={resolveAssetUrl(slide.imageUrl)} alt={slide.headline1} className="max-w-full h-auto mx-auto block" />
          </div>
        )}

        {slideType === "html" && slide.htmlContent && (
          <div className="py-8 w-full" dangerouslySetInnerHTML={{ __html: slide.htmlContent }} />
        )}

        {slideType === "standard" && (
          <div className={`max-w-3xl mx-auto ${isChlorine ? "" : "py-24"}`}>
            {slide.badge && (
              <span 
                className={`inline-block px-4 py-1.5 text-[10px] font-bold tracking-[0.2em] uppercase mb-8 border ${isChlorine ? "border-gray-900 text-gray-900" : "bg-brand-100 text-brand-700 border-transparent rounded-full"}`}
              >
                {slide.badge}
              </span>
            )}

            <h1 className={`font-display font-bold text-gray-900 leading-[1.1] mb-8 ${isChlorine ? "text-6xl sm:text-7xl uppercase tracking-tighter" : "text-5xl sm:text-6xl"}`}>
              {slide.headline1}
              {slide.headline2 && (
                <>{" "}<span className="italic" style={isChlorine ? {} : { color: 'rgb(var(--brand-color))' }}>{slide.headline2}</span></>
              )}
            </h1>

            {slide.body && (
              <p className={`mb-12 max-w-xl mx-auto ${isChlorine ? "text-gray-400 text-sm uppercase tracking-widest font-bold" : "text-lg text-gray-600"}`}>
                {slide.body}
              </p>
            )}

            <div className="flex flex-wrap items-center justify-center gap-6">
              <Link to={slide.ctaUrl} className="btn-primary uppercase tracking-[0.2em] text-[10px] px-10 py-4">
                {slide.ctaLabel}
              </Link>
              {slide.secondaryLabel && slide.secondaryUrl && (
                <Link to={slide.secondaryUrl} className={`btn-secondary uppercase tracking-[0.2em] text-[10px] px-10 py-4 ${isChlorine ? "border-gray-200 text-gray-900 hover:bg-gray-50" : ""}`}>
                  {slide.secondaryLabel}
                </Link>
              )}
            </div>
          </div>
        )}
      </div>

      {total > 1 && (
        <>
          <button onClick={prev} className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/70 p-2 shadow hover:bg-white transition-colors"><ChevronLeft size={20} /></button>
          <button onClick={next} className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/70 p-2 shadow hover:bg-white transition-colors"><ChevronRight size={20} /></button>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`h-1.5 rounded-full transition-all duration-500 ${i === active ? "w-8" : "w-1.5 bg-gray-300"}`}
                style={i === active ? { backgroundColor: 'rgb(var(--brand-color))' } : {}}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
